/**
 * CFleet Dispatcher - TrialCountdownBanner
 *
 * In-app nudge for organizations on a trial with no card on file yet.
 * Renders nothing by default; shows an amber nudge at ~4 days left
 * ("day ~10" of a 14-day trial) and a more urgent rose nudge at ~1 day
 * left ("day ~13"). Both are nudges, not blocking modals — dismissing or
 * ignoring this banner never affects write access; only the actual trial
 * expiry (enforced server-side) does that.
 */

import React from 'react';
import { useMyOrganization, isOrganizationReadOnly } from '@cfleet/api-client';
import { useAuthConfigIfAuthenticated } from '../hooks/useAuthConfig';

const DAY_MS = 24 * 60 * 60 * 1000;

// Thresholds mirror the backend trial-reminder sweeper's day-10/day-13 windows.
const URGENT_THRESHOLD_DAYS = 1;
const NUDGE_THRESHOLD_DAYS = 4;

const TrialCountdownBanner: React.FC = () => {
  const authConfig = useAuthConfigIfAuthenticated();
  const { data: org } = useMyOrganization(authConfig ? { authConfig } : undefined);

  if (!org || org.has_card_on_file || org.subscription_status !== 'trialing' || !org.trial_ends_at) {
    return null;
  }

  // Once the trial has actually lapsed, the read-only banner (surfaced
  // elsewhere) takes over — this countdown is only for the "still time
  // left" window.
  if (isOrganizationReadOnly(org)) {
    return null;
  }

  const daysRemaining = Math.ceil((new Date(org.trial_ends_at).getTime() - Date.now()) / DAY_MS);

  if (daysRemaining > NUDGE_THRESHOLD_DAYS) {
    return null;
  }

  const isUrgent = daysRemaining <= URGENT_THRESHOLD_DAYS;

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid={isUrgent ? 'trial-countdown-banner-urgent' : 'trial-countdown-banner'}
      className={`fixed top-0 inset-x-0 z-[9997] flex items-center justify-center gap-2 px-4 py-1.5 text-xs font-medium border-b ${
        isUrgent
          ? 'bg-rose-50 border-rose-200 text-rose-800'
          : 'bg-amber-50 border-amber-200 text-amber-800'
      }`}
    >
      <span>
        {isUrgent
          ? daysRemaining <= 0
            ? "Your trial ends today — add a payment method to keep your account active."
            : `Your trial ends tomorrow — add a payment method to keep your account active.`
          : `Your trial ends in ${daysRemaining} days — lock in your plan in Settings → Billing.`}
      </span>
    </div>
  );
};

export default TrialCountdownBanner;
